import { Star } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dateTime, Empty, Loadable, money, SearchInput, SegmentedControl, SERVICE_STATUS, useAsync } from '@despachador/ui';
import { api } from '../lib/api';
import { useDriver } from '../store';
import type { HistoryItem } from '../types';

const DONE = ['COMPLETED', 'CLOSED'];
/** What the history page loads: the API's page limit. The filters run over these (no API filters yet). */
const LOADED = 200;

type Period = 'hoy' | '7d' | '30d';
type State = 'todos' | 'completados' | 'cancelados';
const PERIODS: Array<[Period, string]> = [
  ['hoy', 'Hoy'],
  ['7d', '7 días'],
  ['30d', '30 días'],
];
const EARNED: Record<Period, string> = { hoy: 'Ganado hoy', '7d': 'Ganado 7 días', '30d': 'Ganado 30 días' };
const LISTED: Record<Period, string> = { hoy: 'Servicios de hoy', '7d': 'Servicios de los últimos 7 días', '30d': 'Servicios de los últimos 30 días' };

const when = (s: HistoryItem) => new Date(s.completed_at ?? s.created_at);
const fold = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** The trip falls in the period: today (the phone's calendar day) or the last 7 / 30 days. */
function inPeriod(s: HistoryItem, p: Period, now: Date): boolean {
  const t = when(s);
  if (p === 'hoy') return t.toDateString() === now.toDateString();
  return now.getTime() - t.getTime() <= (p === '7d' ? 7 : 30) * 86_400_000;
}

/**
 * What the driver did and earned: the money first, then the trips, newest first (DRV-010). The
 * period, the search and the state live in the URL, so reloading or going back keeps them.
 */
export function Trips() {
  const { me } = useDriver();
  const q = useAsync(() => api.get<HistoryItem[]>(`/driver/services?limit=${LOADED}`));
  const [params, setParams] = useSearchParams();
  // «Quitar filtros» clears the URL; the search box keeps its own text, so it is mounted again
  const [searchKey, setSearchKey] = useState(0);
  if (!me) return null;
  const d = me.dashboard;
  const rating = me.profile.rating;
  const period: Period = (['hoy', '7d', '30d'] as const).find((p) => p === params.get('periodo')) ?? 'hoy';
  const state: State = (['todos', 'completados', 'cancelados'] as const).find((s) => s === params.get('estado')) ?? 'todos';
  const search = params.get('q') ?? '';
  const set = (key: string, value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const now = new Date();
  const all = q.data ?? [];
  const inRange = all.filter((s) => inPeriod(s, period, now));
  const completed = inRange.filter((s) => DONE.includes(s.status));
  // today and the last 7 days come from the server; 30 days is summed here from what was loaded
  const earned = period === 'hoy' ? d.earnings_today_minor : period === '7d' ? d.earnings_week_minor : completed.reduce((sum, s) => sum + (s.fare_total_minor ?? 0), 0);
  const servicesCount = period === 'hoy' ? d.completed_today : completed.length;
  // 30 days from a full page whose oldest trip is still inside the period: there may be more
  const partial = period === '30d' && all.length >= LOADED && inPeriod(all[all.length - 1]!, '30d', now);

  const needle = fold(search.trim());
  const matched = needle ? inRange.filter((s) => fold(`${s.code} ${s.origin_text ?? ''} ${s.destination_text ?? ''}`).includes(needle)) : inRange;
  const counts = { todos: matched.length, completados: matched.filter((s) => DONE.includes(s.status)).length, cancelados: matched.filter((s) => !DONE.includes(s.status)).length };
  const shown = state === 'todos' ? matched : matched.filter((s) => DONE.includes(s.status) === (state === 'completados'));

  return (
    <div className="trips-page">
      <section className="earnings" aria-labelledby="trips-title">
        <h1 id="trips-title" className="page-title">
          Viajes
        </h1>
        <SegmentedControl label="Período" options={PERIODS} value={period} onChange={(v) => set('periodo', v, 'hoy')} />
        <div className="earnings-main">
          <div>
            <span className="micro-title">{EARNED[period]}</span>
            {earned > 0 || period !== 'hoy' ? <b className="money-lg">{money(earned, d.currency)}</b> : <b className="earnings-none">Aún no has completado servicios hoy</b>}
          </div>
          {(earned > 0 || period !== 'hoy') && (
            <span className="earnings-count">
              {servicesCount} {servicesCount === 1 ? 'servicio' : 'servicios'}
            </span>
          )}
        </div>
        {partial && <p className="earnings-note">Calculado con tus últimos {LOADED} servicios.</p>}
        <dl className="earnings-more">
          {period === 'hoy' && (
            <div>
              <dt>Ganado en 7 días</dt>
              <dd>{money(d.earnings_week_minor, d.currency)}</dd>
            </div>
          )}
          <div>
            <dt>Completados desde que empezaste</dt>
            <dd>{d.completed_total}</dd>
          </div>
          <div>
            <dt>Ofertas aceptadas (30 d)</dt>
            <dd>
              {d.offers30d.accepted} <small>de</small> {d.offers30d.offered}
            </dd>
          </div>
          <div>
            <dt>Tu calificación</dt>
            <dd>
              {rating === null ? (
                <small className="plain">Aún sin calificaciones</small>
              ) : (
                <>
                  <Star size={16} aria-hidden className="star" /> {rating.toFixed(1)}
                  <span className="sr-only"> de 5</span>
                </>
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="trips-recent" aria-labelledby="recent-title">
        <div className="trips-filters">
          <h2 id="recent-title" className="micro-title">
            {LISTED[period]}
          </h2>
          <SearchInput key={searchKey} label="Buscar servicio por código o dirección" placeholder="Buscar código o dirección" value={search} onChange={(v) => set('q', v, '')} />
          {/* the same control as the period above: one selector style per screen (UX-028) */}
          <SegmentedControl
            label="Estado del servicio"
            value={state}
            onChange={(v) => set('estado', v, 'todos')}
            options={[
              ['todos', <>Todos <span className="count">{counts.todos}</span></>],
              ['completados', <>Completados <span className="count">{counts.completados}</span></>],
              ['cancelados', <>Cancelados <span className="count">{counts.cancelados}</span></>],
            ]}
          />
        </div>
        <Loadable state={q}>
          {() =>
            !all.length ? (
              <div className="page-pad">
                <Empty title="Aún no tienes servicios.">Cuando termines tu primer servicio aparecerá aquí con su tarifa.</Empty>
              </div>
            ) : !shown.length ? (
              <div className="page-pad">
                <Empty
                  title="No hay servicios con estos filtros."
                  action={
                    search || state !== 'todos' ? (
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          const next = new URLSearchParams(params);
                          next.delete('q');
                          next.delete('estado');
                          setParams(next, { replace: true });
                          setSearchKey((k) => k + 1);
                        }}
                      >
                        Quitar filtros
                      </button>
                    ) : undefined
                  }
                >
                  {search ? `Nada coincide con «${search}» en este período.` : 'Prueba con otro período.'}
                </Empty>
              </div>
            ) : (
              <ul className="trip-list">
                {shown.map((s) => {
                  const done = DONE.includes(s.status);
                  return (
                    <li key={s.id} className={done ? undefined : 'not-done'}>
                      <span className="trip-list-code">
                        <span className="code">{s.code}</span>
                        {/* completed is the normal case: only what did not end well gets a word */}
                        {!done && (
                          <span className="badge danger">
                            <span className="dot" aria-hidden /> {SERVICE_STATUS[s.status]?.label ?? s.status}
                          </span>
                        )}
                      </span>
                      <b className="trip-list-money">{done ? money(s.fare_total_minor, s.currency) : '—'}</b>
                      <span className="trip-list-route">
                        {s.origin_text ?? '—'} → {s.destination_text ?? '—'}
                      </span>
                      <span className="trip-list-date">{dateTime(when(s).toISOString())}</span>
                    </li>
                  );
                })}
              </ul>
            )
          }
        </Loadable>
      </section>
    </div>
  );
}
